<script>
export default {
    methods: {
        async checkBandwithDownload(url, chunksToDownload = 20) {
            this.cancelled = false;
            this.abortController = new AbortController();

            for (let i = 1; i <= chunksToDownload; i++) {
                if (this.cancelled) break;

                const startTime = Date.now(); // Début du téléchargement

                try {
                    let chunk = await fetch(url, { signal: this.abortController.signal, cache: 'no-store' });

                    if (chunk.status == 200) {
                        let blob = await chunk.blob();

                        const endTime = Date.now(); // Fin du téléchargement
                        const duration = (endTime - startTime) / 1000; // Temps en secondes

                        const bytesReceived = blob.size; // Taille des données reçues en octets
                        const bitsReceived = bytesReceived * 8; // En bits
                        const bandwidth = (bitsReceived / duration) / 1_000_000; // Bande passante en Mbps

                        this.downloadSpeeds.push(bandwidth);
                        this.averageDownloadSpeed = this.downloadSpeeds.reduce((partialSum, a) => partialSum + a, 0) / i;

                        this.downloadChart.data.labels.push('');
                        this.downloadChart.data.datasets[0].data.push(bandwidth.toFixed(2));
                        this.downloadChart.data.datasets[1].data.push(this.averageDownloadSpeed.toFixed(2));
                        this.downloadChart.update();
                    }
                } catch (e) {
                    if (e.name === 'AbortError') break;
                    throw e;
                }

                await new Promise((resolve) =>
                    setTimeout(resolve, 500)
                );
            }

            if (!this.cancelled) {
                this.done = true;
                this.$emit('done');
            }
        },
        cancel() {
            this.cancelled = true;
            this.abortController?.abort();
        },
        reset() {
            this.cancel();
            this.done = false;
            this.cancelled = false;
            this.downloadSpeeds = [];
            this.averageDownloadSpeed = 0;
            if (this.downloadChart) {
                this.downloadChart.data.labels = [];
                this.downloadChart.data.datasets[0].data = [];
                this.downloadChart.data.datasets[1].data = [];
                this.downloadChart.update();
            }
        }
    },
    data() {
        return {
            done: false,
            cancelled: false,
            abortController: null,
            downloadSpeeds: [],
            averageDownloadSpeed: 0,
            downloadChart: null,
            bandwidth: {
                labels: [],
                datasets: [
                    {
                        label: 'Hits',
                        // cubicInterpolationMode: 'monotone',
                        borderColor: "#F78D34",
                        // backgroundColor: "#FCD7B780",
                        borderDash: [5, 5],
                        // fill: true,
                    },
                    {
                        label: 'Average',
                        cubicInterpolationMode: 'monotone',
                        // tension: 0.1,
                        borderColor: "#F78D34",
                        backgroundColor: "#FCD7B780",
                        tension: 0.1,
                        fill: true,
                    }]
            }
        };
    },
    computed: {
        chartYMin() {
            return this.downloadChart?.scales?.y?.min;
        },
        chartYMax() {
            return this.downloadChart?.scales?.y?.max;
        },
        downloadAnnotationBottom() {
            if (!this.downloadChart?.scales?.y) return 0;

            if(this.downloadAnnotationValue < this.downloadChart.scales.y.min)
            {
                return 0;
            }

            var h = this.downloadChart.scales.y.max - this.downloadChart.scales.y.min;

            var l = this.downloadAnnotationValue - this.downloadChart.scales.y.min;

            return Math.min(100, l * 100 / h);
        },
        status() {
            if (!this.done) {
                return 'pending';
            }
            else if (this.averageDownloadSpeed > this.downloadAnnotationValue) {
                return 'ok';
            }
            else {
                return 'ko';
            }
        }
    },
    props: {
        downloadAnnotationLabel:
        {
            default: 'Minimum required (6Mbps)'
        },
        downloadAnnotationValue:
        {
            default: 6
        },
        url: {},
        enabled:
        {
            default: false
        },
    },
    translate25Context: 'troubleshooting',
    watch: {
        enabled: function () {
            if (this.enabled) {
                this.checkBandwithDownload(this.url + '/download');
            }
        }
    },
    mounted() {
        this.downloadChart = new Chart(
            this.$refs['downloadChart'],
            {
                type: 'line',
                data: this.bandwidth,
                options: {
                    animation: {
                        duration: 500, // durée en ms
                        easing: 'easeOutQuart' // autre exemple : 'linear', 'easeInOutQuad', etc.
                    },
                    plugins: {
                        legend: { 
                            display: false,
                            // position: 'bottom',
                        }
                    },
                    interaction: {
                        mode: 'index',
                        intersect: false
                    },
                    scales: {
                        x: {
                            display: false,
                        },
                        y: {
                            display: false,
                            suggestedMin: 0
                        },
                    },
                    layout: {
                        padding: 0
                    },
                    radius: 0,
                }
            }
        );

        this.$refs.legend.innerHTML = this.downloadChart.data.datasets.map(dataset => {

            const isDashed = dataset.borderDash ? true : false;
            return `
                <div style="margin-bottom: 4px; display: flex; align-items: center;">
                <span style="
                    width: 16px;
                    height: 16px;
                    ${isDashed ? `border: 3px dashed ${dataset.borderColor}; background: ${dataset.backgroundColor};` : `border: 3px solid ${dataset.borderColor}; background-color: ${dataset.backgroundColor};`}
                    display: inline-block;
                    margin-right: 8px;
                "></span>
                ${dataset.label}
                </div>
            `;
        }).join('');

        if (this.enabled) {
            this.checkBandwithDownload(this.url + '/download');
        }
    }
};
</script>

<template>
    <div class="troubleshooting-bandwith"
        :class="{ danger: (averageDownloadSpeed < downloadAnnotationValue), ok: averageDownloadSpeed != 0 && averageDownloadSpeed > downloadAnnotationValue }">

        <div class="troubleshooting-bandwith-title-and-value">
            <troubleshooting-page-status :status="status">
                <template #content>
                    <div class="troubleshooting-bandwith-title">{{ $vt('Reception') }}
                    </div>
                </template>
            </troubleshooting-page-status>
            <div class="troubleshooting-bandwith-value">{{ averageDownloadSpeed.toFixed(2) }} Mbps</div>
        </div>
        <div class="troubleshooting-bandwith-chart">
            <!-- <div class="troubleshooting-bandwith-chart-y-min">{{ chartYMin }} Mbps</div>
            <div class="troubleshooting-bandwith-chart-y-max">{{ chartYMax }} Mbps</div> -->
            <div class="troubleshooting-bandwith-annotation" v-if="averageDownloadSpeed != 0"
                :style="{ bottom: downloadAnnotationBottom + '%' }">
                <div class="troubleshooting-bandwith-annotation-label">{{ $vt(downloadAnnotationLabel) }}</div>
                <div class="troubleshooting-bandwith-annotation-line"></div>
            </div>
            <canvas ref="downloadChart"></canvas>
        </div>
        <div ref="legend" class="troubleshooting-bandwith-legend-container"></div>
        <f-hint type="negative" v-if="averageDownloadSpeed != 0 && averageDownloadSpeed < downloadAnnotationValue"
            icon="fa6-triangle-exclamation">
            <template #message>{{ $vt('Your download speed does not meet the minimum requirement (6Mbps)') }}</template>
        </f-hint>
    </div>
</template>

<style lang="scss"></style>
